
import React from 'react';
import { Ticket, TicketStatus, TicketPriority, AppSettings, Employee, RepairChecklist } from '../types';

interface TicketBoardCardProps {
  ticket: Ticket;
  appSettings: AppSettings;
  currentUser: Employee | null;
  onDragStart: (e: React.DragEvent<HTMLDivElement>) => void;
  onOpenUpdateStatusModal: (ticketId: string, currentStatus: TicketStatus) => void;
  onViewCustomerProfile: (customerName: string) => void;
  repairChecklists: RepairChecklist[]; // New prop (for potential future display)
  onUpdateTicketChecklistProgress: (ticketId: string, checklistItemId: string, completed: boolean) => void; // New prop (for potential future interaction)
}

const getPriorityClass = (priority: TicketPriority, appSettings: AppSettings): string => {
  switch (priority) {
    case TicketPriority.HIGH:
      return 'border-l-4 border-red-500';
    case TicketPriority.MEDIUM:
      return `border-l-4 border-[var(--color-accent)]`;
    case TicketPriority.LOW:
      return 'border-l-4 border-emerald-500';
    default:
      return 'border-l-4 border-slate-400';
  }
};

const GripVerticalIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l.001 14M15 5l.001 14" />
  </svg>
);

export const TicketBoardCard: React.FC<TicketBoardCardProps> = ({ 
    ticket, 
    appSettings,
    currentUser, 
    onDragStart,
    // repairChecklists, // Received, but not used for display yet
    // onUpdateTicketChecklistProgress, // Received, but not used for interaction yet
    // onOpenUpdateStatusModal, 
    // onViewCustomerProfile    
}) => {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      className={`bg-white p-3 rounded-md shadow-md hover:shadow-lg transition-shadow cursor-grab flex ${getPriorityClass(ticket.priority, appSettings)}`}
      aria-roledescription="Draggable ticket card"
      aria-label={`Ticket ${ticket.id}: ${ticket.subject}, Customer ${ticket.customer}, Priority ${ticket.priority}`}
    >
        <div className="flex-shrink-0 pr-2 flex items-center justify-center opacity-30 group-hover:opacity-100">
            <GripVerticalIcon className="w-4 h-4 text-slate-400" />
        </div>
        <div className="flex-grow">
            <div className="flex justify-between items-start mb-1">
                <h4 className="text-xs font-semibold text-[var(--color-primary)]">{ticket.id}</h4>
                 <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-full 
                    ${ticket.priority === TicketPriority.HIGH ? 'bg-red-100 text-red-700' :
                    ticket.priority === TicketPriority.MEDIUM ? 'bg-amber-100 text-amber-700' :
                    'bg-emerald-100 text-emerald-700'}`}>
                    {ticket.priority}
                </span>
            </div>
            <p className="text-sm text-slate-800 font-medium leading-tight mb-1.5 truncate" title={ticket.subject}>
                {ticket.subject}
            </p>
            <p className="text-xs text-slate-500 truncate" title={ticket.customer}>
                Customer: {ticket.customer}
            </p>
            {ticket.deviceModel && (
                 <p className="text-xs text-slate-500 truncate" title={ticket.deviceModel}>
                    Device: {ticket.deviceModel}
                </p>
            )}
             <p className="text-xs text-slate-400 mt-1">
                Created: {new Date(ticket.createdAt).toLocaleDateString('en-CA')}
            </p>
        </div>
    </div>
  );
};
